import React from 'react';
import { STEPS } from '../constants';
import RevealOnScroll from './RevealOnScroll';
import { UserPlus, ShoppingCart, Package, ArrowRight } from 'lucide-react';

const HowItWorks: React.FC = () => {
  // Map icons to steps matching the visual style requested
  const getIcon = (index: number) => {
    switch (index) {
      case 0: return <UserPlus className="w-6 h-6 md:w-8 md:h-8" />;
      case 1: return <ShoppingCart className="w-6 h-6 md:w-8 md:h-8" />;
      case 2: return <Package className="w-6 h-6 md:w-8 md:h-8" />;
      default: return <UserPlus className="w-6 h-6 md:w-8 md:h-8" />;
    }
  };

  return (
    <section id="how-it-works" className="py-12 md:py-24 bg-slate-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        <div className="text-center mb-10 md:mb-16">
          <RevealOnScroll>
            <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 mb-3 md:mb-4">How OTL Express Works</h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Get your items from Sri Lanka to the Maldives in 3 simple steps.
            </p>
          </RevealOnScroll>
        </div>

        <div className="space-y-8 md:space-y-12">
          {STEPS.map((step, index) => {
            // Determine alignment: Even = Image Left, Odd = Image Right
            const isEven = index % 2 === 0;

            return (
              <RevealOnScroll key={step.id} delay={index * 150}>
                <div className="bg-white rounded-2xl md:rounded-3xl shadow-lg border border-slate-100 overflow-hidden flex flex-col md:flex-row group hover:shadow-2xl transition-shadow duration-300">

                  {/* Image Section */}
                  <div className={`relative w-full md:w-1/2 h-56 sm:h-64 md:h-auto overflow-hidden ${isEven ? 'md:order-1' : 'md:order-2'}`}>
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />

                    {/* Number Badge */}
                    <div className="absolute top-4 left-4 md:top-6 md:left-6 z-10">
                      <div className="w-10 h-10 md:w-14 md:h-14 bg-brand-600 rounded-full flex items-center justify-center shadow-lg border-2 md:border-4 border-white/20 backdrop-blur-sm">
                        <span className="text-white font-bold text-lg md:text-xl">0{step.id}</span>
                      </div>
                    </div>

                    {/* Overlay gradient for mobile text readability if needed, though mostly for aesthetic */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent md:hidden"></div>
                  </div>

                  {/* Content Section */}
                  <div className={`w-full md:w-1/2 p-6 md:p-12 lg:p-16 flex flex-col justify-center ${isEven ? 'md:order-2' : 'md:order-1'}`}>
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-brand-50 rounded-xl md:rounded-2xl flex items-center justify-center text-brand-600 mb-4 md:mb-6 shadow-sm group-hover:bg-brand-600 group-hover:text-white transition-colors duration-300">
                      {getIcon(index)}
                    </div>

                    <h3 className="text-xl md:text-3xl font-bold text-slate-900 mb-3 md:mb-4">
                      {step.title}
                    </h3>

                    <p className="text-slate-600 text-sm md:text-lg leading-relaxed mb-4 md:mb-6">
                      {step.description}
                    </p>

                    {index === 0 && (
                      <div className="mt-2">
                        <a onClick={() => window.location.href = "/auth/login"} href="#" className="inline-flex items-center text-brand-600 font-bold hover:text-brand-700 transition-colors text-sm md:text-base">
                          Create Free Account <ArrowRight className="ml-2 w-4 h-4" />
                        </a>
                      </div>
                    )}
                  </div>

                </div>
              </RevealOnScroll>
            );
          })}
        </div>


        {/* Instagram Reel Embed */}
        <div className="flex justify-center mt-8">
          <iframe
            src="https://otl.nyc3.cdn.digitaloceanspaces.com/otlexp.com%20-%20FE/0tL%2022.mp4"
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="Instagram Reel"
            className="rounded-2xl border border-slate-200 shadow-lg w-full max-w-xl aspect-[9/16] bg-white"
            style={{ minHeight: 500 }}
          ></iframe>
        </div>
        <p className="text-center text-slate-500 mt-4 italic">Watch how easy it is to start shipping.</p>
        <div className="mt-10 md:mt-16 text-center">
          <button className="bg-brand-600 hover:bg-brand-700 text-white text-base md:text-lg font-bold py-3 px-8 md:py-4 md:px-10 rounded-full shadow-lg hover:shadow-brand-500/30 transition-all transform hover:-translate-y-1">
            Start Shipping Today
          </button>
        </div>

      </div>
    </section>
  );
};

export default HowItWorks;